<script setup>
import {ref, reactive, computed, onMounted, watch} from "vue";
import useForm from "@/composables/useForm";
import useNeedUpgrade from "@/composables/useNeedUpgrade";
import {useStore} from "vue2-helpers/vuex";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import Multiselect from "vue-multiselect";
import tasksService from "@/services/tasks.service";
import useStoreTrigger from "@/composables/useStoreTrigger";
import startupsService from "@/services/startups.service";
import useDebounce from "@/composables/useDebounce";
import {getLogo} from "@/helpers/logo";
import DatePicker from "vue2-datepicker";
import Avatar from "vue-avatar";
import trackersService from "@/services/trackers.service";
import ecosystemsService from "@/services/ecosystems.service";
import {encodeDateForServer} from "@/helpers/date";

const typeStartup = 'startup';
const typeTracker = 'tracker';
const typeEcosystem = 'ecosystem';

const {startupId, ecosystemId, trackerId} = defineProps(['startupId', 'ecosystemId', 'trackerId']);
const emit = defineEmits(['closed']);

const store = useStore();

const {showNeedUpgrade} = useNeedUpgrade();
const storeTrigger = useStoreTrigger();
const debounce = useDebounce();

const id = ref(0);
const type = ref('startup');
const showModal = ref(false);
const loading = ref(false);

const startup = ref(null);
const tracker = ref(null);
const ecosystem = ref(null);
const user = ref(null);
const date = ref(null);

const loadingStartupsList = ref(false);
const loadingEcosystemsList = ref(false);

const startupsList = ref([]);
const trackersList = ref([]);
const ecosystemsList = ref([]);
const usersList = ref([]);

const lastAddedStartups = ref([]);

const form = reactive({
    title: "",
    description: "",
    startup_id: null,
    tracker_id: null,
    ecosystem_id: null,
    user_id: null,
    date: null,
    private: false,
})

const {resetForm, populateForm} = useForm(form);

const canSave = computed(() =>
    form.title.length
    && (
        (type.value === typeStartup && form.startup_id)
        || (type.value === typeTracker && form.tracker_id)
        || (type.value === typeEcosystem && form.ecosystem_id)
    )
    && form.user_id
);

watch(type, (value) => {
    if (value === typeTracker) {
        startup.value = null;
        ecosystem.value = null;
    } else if (value === typeEcosystem) {
        startup.value = null;
        tracker.value = null;
    } else if (value === typeStartup) {
        tracker.value = null;
        ecosystem.value = null;
    }
});

watch(startup, (value) => {
    form.startup_id = value?.id ?? null;
});

watch(ecosystem, (value) => {
    form.ecosystem_id = value?.id ?? null;
});

watch(tracker, (value) => {
    form.tracker_id = value?.id ?? null;

    getUsers();
});

watch(user, (value) => {
    form.user_id = value?.id ?? null;
});

watch(date, (value) => {
    form.date = value ? encodeDateForServer(value) : null;
});

watch(showModal, (value) => {
    if (!value) {
        emit('closed');
    }
});

onMounted(() => {
    getLastAddedStartups();
    getTrackers();

    if (store.getters.isEnterprise) {
        getInitEcosystems();
    }
});

async function getLastAddedStartups() {
    const {data} = await startupsService.getLastAddedStartups();

    lastAddedStartups.value = data;
    startupsList.value = data;
}

async function getInitEcosystems() {
    const {data} = await ecosystemsService.getList();
    ecosystemsList.value = data;
}

async function getStartups(search) {
    debounce(async () => {
        loadingStartupsList.value = true;
        const {data} = await startupsService.getList(search);

        startupsList.value = data;
        loadingStartupsList.value = false;
    });
}

async function getEcosystems(search) {
    debounce(async () => {
        loadingEcosystemsList.value = true;
        const {data} = await ecosystemsService.getList(search);

        ecosystemsList.value = data;
        loadingEcosystemsList.value = false;
    });
}

async function getTrackers() {
    const {data} = await trackersService.getList();

    trackersList.value = data;
}

async function getUsers() {
    usersList.value = [];

    const {data} = await tasksService.getUsersList(form.tracker_id ?? 0);
    const currentUser = data.find(item => item.id === store.state.user.id);

    if (currentUser) {
        usersList.value.push(currentUser);
    }

    usersList.value = [...usersList.value, ...data.filter(item => item.id !== store.state.user.id)];

    if (form.user_id) {
        if (!usersList.value.some(item => item.id === form.user_id && item['$isDisabled'] === undefined)) {
            form.user_id = null;
            user.value = null;
        } else {
            user.value = usersList.value.find(item => item.id === form.user_id);
        }
    }
}

function reset() {
    resetForm();

    user.value = null;
    startup.value = null;
    tracker.value = null;
    date.value = null;
    ecosystem.value = null;

    if (startupId) {
        form.startup_id = startupId;
    }

    if (trackerId) {
        type.value = typeTracker;
        form.tracker_id = trackerId;
    } else if (ecosystemId) {
        type.value = typeEcosystem;
        form.ecosystem_id = ecosystemId;
    } else {
        type.value = typeStartup;
    }

    if (id.value) {
        getData();
    } else {
        getUsers();
    }
}

async function getData() {
    let {data} = await tasksService.getEdit(id.value);

    populateForm(data);

    if (form.startup_id) {
        type.value = typeStartup;
        let {data} = await startupsService.getStartupDataForSelect(form.startup_id);

        if (data) {
            startup.value = data;
        }
    }

    if (form.ecosystem_id) {
        type.value = typeEcosystem;
        let {data} = await ecosystemsService.getDataForSelect(form.ecosystem_id);

        if (data) {
            ecosystem.value = data;
        }
    }

    if (form.tracker_id) {
        type.value = typeTracker;

        if (trackersList.value.length) {
            let index = trackersList.value.findIndex(item => item.id === form.tracker_id);

            if (index >= 0) {
                tracker.value = trackersList.value[index]
            }
        }
    } else {
        getUsers();
    }
}

async function save() {
    loading.value = true;
    await tasksService.save(id.value, form);

    loading.value = false;
    showModal.value = false;
    storeTrigger('setNeedUpdateTasks');
    storeTrigger('setNeedUpdateTrackerLogs');
}

function open(taskId = 0) {
    if (!store.getters.isPremium) {
        showNeedUpgrade('tasks');
        return;
    }

    id.value = taskId;
    showModal.value = true;
    reset();
}

defineExpose({
    open
});

</script>

<template>
    <b-modal v-model="showModal" :title="`${id ? 'Edit' : 'Add'} task`" modal-class="modal-w-md">
        <div class="form__item">
            <h6 class="heading6 mb-1">Task Title*</h6>
            <b-form-input class="form-control--mid mb-3" v-model="form.title"></b-form-input>
        </div>

        <div class="form__item">
            <h6 class="heading6 mb-1">Task Description</h6>
            <b-form-textarea class="form-control--mid mb-3" v-model="form.description"/>
        </div>

        <template v-if="!startupId && !trackerId && !ecosystemId">
            <div class="form__item d-flex mb-3">
                <div class="input-radio mr-3">
                    <input id="pr1" type="radio" v-model="type" value="startup">
                    <label for="pr1">Startup</label>
                </div>

                <div class="input-radio mr-3">
                    <input id="pr2" type="radio" v-model="type" value="tracker">
                    <label for="pr2">Tracker</label>
                </div>

                <div class="input-radio">
                    <input id="pr3" type="radio" v-model="type" value="ecosystem">
                    <label for="pr3">Ecosystem</label>
                </div>
            </div>

            <div class="form__item" v-if="type === typeStartup">
                <h6 class="heading6 mb-1">Startup*</h6>

                <Multiselect
                    class="multiselect--complex multiselect-mid mb-3"
                    v-model="startup"
                    :options="startupsList"
                    select-label=""
                    deselect-label=""
                    label="name"
                    track-by="name"
                    placeholder="Start typing..."
                    :loading="loadingStartupsList"
                    @search-change="getStartups"
                >
                    <template #option="props">
                        <img :src="getLogo(props.option.logo)" style="width:40px" alt=""> {{ props.option.name }}
                    </template>
                </Multiselect>
            </div>

            <div class="form__item" v-if="type === typeTracker">
                <h6 class="heading6 mb-1">Tracker*</h6>

                <Multiselect
                    v-model="tracker"
                    :options="trackersList"
                    select-label=""
                    deselect-label=""
                    placeholder="Type to search your trackers"
                    label="name"
                    track-by="name"
                    class="multiselect-mid selectbox selectbox-mid mb-3"
                />
            </div>

            <div class="form__item" v-if="type === 'ecosystem'">
                <h6 class="heading6 mb-1">Ecosystem*</h6>

                <Multiselect
                    v-model="ecosystem"
                    :options="ecosystemsList"
                    select-label=""
                    deselect-label=""
                    placeholder="Type to search ecosystem"
                    :loading="loadingEcosystemsList"
                    label="name"
                    track-by="name"
                    class="multiselect--complex multiselect-mid mb-3"
                    @search-change="getEcosystems"
                >
                    <template #option="props">
                        <img :src="getLogo(props.option.logo)" style="width:40px" alt=""> {{ props.option.name }}
                    </template>
                </Multiselect>
            </div>

        </template>

        <div class="form__item">
            <h6 class="heading6 mb-1">Assignee*</h6>

            <Multiselect
                class="multiselect-mid mb-3"
                v-model="user"
                :options="usersList"
                select-label=""
                deselect-label=""
                label="name"
                track-by="name"
                placeholder="Select a user"
            >
                <template #option="props">
                    <div class="d-flex align-items-center">
                        <div class="mr-2">
                            <Avatar
                                :username="props.option.name"
                                :src="props.option.avatar"
                                :size="30">
                            </Avatar>
                        </div>

                        {{ props.option.name }}
                    </div>
                </template>
            </Multiselect>
        </div>

        <div class="form__item">
            <h6 class="heading6 mb-1">Due Date (optional)</h6>
            <date-picker v-model="date" placeholder="Select a date" format = "MM/DD/YYYY" class="mb-2 w-100"></date-picker>
        </div>

        <div class="form__item">
            <div class="input-checkbox mt-2">
                <input id="private" type="checkbox" v-model="form.private">
                <label for="private">Make private (Only you, the assignee and admins can view)</label>
            </div>
        </div>

        <template #modal-footer>
            <PrimaryButton
                :loading="loading"
                loading-caption="Saving..."
                :caption="id ? 'Update' : 'Add'"
                :disabled="!canSave"
                :data-track="id ? 'edit-task' : 'add-task'"
                @click="save"
            />
        </template>
    </b-modal>
</template>

<style scoped>

</style>
